import React from 'react';

/* ── HTTP Status Codes ─────────────────────────────────────── */

type HttpStatusRow = {
  code: string;
  badge: string;
  name: string;
  description: string;
  /** ไม่ใส่ = แสดง "-" (กรณีที่ไม่ต้องแก้อะไร) */
  fix?: string;
};

const HTTP_STATUSES: HttpStatusRow[] = [
  { code: '200', badge: 'bg-emerald-100 text-emerald-700', name: 'OK', description: 'Request สำเร็จ' },
  { code: '201', badge: 'bg-emerald-100 text-emerald-700', name: 'Created', description: 'สร้างข้อมูลสำเร็จ (Order, Payout)' },
  { code: '400', badge: 'bg-amber-100 text-amber-700', name: 'Bad Request', description: 'ข้อมูลไม่ถูกต้อง หรือ field ที่จำเป็นหายไป', fix: 'ตรวจสอบ request body ว่าครบ required fields' },
  { code: '401', badge: 'bg-rose-100 text-rose-700', name: 'Unauthorized', description: 'API Key/Secret ไม่ถูกต้อง หรือ Signature ไม่ valid', fix: 'ตรวจสอบ API Key, Secret และ Signature algorithm' },
  { code: '403', badge: 'bg-rose-100 text-rose-700', name: 'Forbidden', description: 'ไม่มีสิทธิ์เข้าถึง หรือ IP ไม่ได้อยู่ใน whitelist', fix: 'ตรวจสอบ IP Whitelist และ permission ของ API Key' },
  { code: '404', badge: 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200', name: 'Not Found', description: 'ไม่พบ resource ที่ร้องขอ', fix: 'ตรวจสอบ URL path และ order/payout number' },
  { code: '409', badge: 'bg-amber-100 text-amber-700', name: 'Conflict', description: 'Duplicate reference_id หรือข้อมูลขัดแย้ง', fix: 'ใช้ reference_id ที่ไม่ซ้ำกัน' },
  { code: '422', badge: 'bg-orange-100 text-orange-700', name: 'Unprocessable', description: 'ข้อมูลถูกต้องแต่ไม่สามารถดำเนินการได้ เช่น ยอดเงินไม่พอ', fix: 'ตรวจสอบ balance และเงื่อนไขการทำรายการ' },
  { code: '429', badge: 'bg-purple-100 text-purple-700', name: 'Too Many Requests', description: 'เรียก API บ่อยเกินไป (Rate Limit)', fix: 'รอสักครู่แล้วลองใหม่ หรือลด request rate' },
  { code: '500', badge: 'bg-red-100 text-red-700', name: 'Server Error', description: 'ระบบเกิดข้อผิดพลาด', fix: 'ลองใหม่อีกครั้ง หากยังเกิดขึ้น ติดต่อ support' },
];

export const HTTP_STATUS_HEADERS = ['Status', 'Name', 'Description', 'วิธีแก้ไข'];

export const HTTP_STATUS_ROWS: React.ReactNode[][] = HTTP_STATUSES.map(row => [
  <span className={`px-2 py-1 rounded-lg text-xs font-bold font-mono ${row.badge}`}>{row.code}</span>,
  <span className="font-medium text-gray-700 dark:text-slate-200">{row.name}</span>,
  <span className="text-gray-500 dark:text-slate-400 text-xs">{row.description}</span>,
  row.fix
    ? <span className="text-gray-500 dark:text-slate-400 text-xs">{row.fix}</span>
    : <span className="text-gray-400 dark:text-slate-500 text-xs">-</span>,
]);

/* ── Business error codes ──────────────────────────────────── */

const BUSINESS_ERRORS: Array<{ code: string; http: string; description: string }> = [
  { code: 'INVALID_AMOUNT', http: '400', description: 'จำนวนเงินไม่ถูกต้อง (ต้องมากกว่า 0)' },
  { code: 'INVALID_BANK', http: '400', description: 'ชื่อธนาคารไม่ถูกต้อง ใช้ short_name จาก Banks API' },
  { code: 'DUPLICATE_REFERENCE', http: '409', description: 'reference_id นี้ถูกใช้ไปแล้ว' },
  { code: 'INSUFFICIENT_BALANCE', http: '422', description: 'ยอดเงินใน wallet ไม่เพียงพอ' },
  { code: 'MERCHANT_INACTIVE', http: '403', description: 'บัญชี merchant ถูกระงับ' },
  { code: 'SIGNATURE_INVALID', http: '401', description: 'Signature ไม่ถูกต้อง (V2)' },
  { code: 'TIMESTAMP_EXPIRED', http: '401', description: 'Timestamp หมดอายุ (เกิน 5 นาที)' },
  { code: 'ORDER_EXPIRED', http: '400', description: 'Order หมดอายุแล้ว' },
  { code: 'NO_BANK_AVAILABLE', http: '422', description: 'ไม่มีบัญชีธนาคารว่างรับออเดอร์' },
];

export const BUSINESS_ERROR_HEADERS = ['Error Code', 'HTTP', 'Description'];

export const BUSINESS_ERROR_ROWS: React.ReactNode[][] = BUSINESS_ERRORS.map(row => [
  <code className="text-rose-600 font-mono text-xs">{row.code}</code>,
  <span className="font-mono text-xs">{row.http}</span>,
  <span className="text-gray-500 dark:text-slate-400 text-xs">{row.description}</span>,
]);

/** ตัวอย่างรูปแบบ error response */
export const ERROR_RESPONSE_SAMPLE = `{
  "success": false,
  "message": "Error description in Thai or English",
  "error": "ERROR_CODE",
  "details": {
    "field": "amount",
    "reason": "Must be greater than 0"
  }
}`;
